import React, { useEffect, useState } from "react";
import { MAPILLARY_TOKEN, MAPILLARY_ENABLED } from "../config";
import "../styles/Mapillary.css";

// Mapillary Graph API base.
const GRAPH_URL = "https://graph.mapillary.com/images";

// Half-widths (in degrees) of the bounding box we search around a facility,
// tried smallest-first so we prefer the closest imagery. The Graph API
// requires each bbox to be smaller than 0.01 degrees square, so every full
// width here (2 x half-width) stays comfortably under that limit.
// ~0.001 deg of latitude ≈ 111 m.
const BBOX_HALF_WIDTHS = [0.0015, 0.003, 0.0045];

// Simple in-memory cache so the popup and the modal for the same facility
// don't each fire their own request. Keyed by rounded lat/lng.
const cache = new Map();

const keyFor = (lat, lng) => `${lat.toFixed(5)},${lng.toFixed(5)}`;

// Squared planar distance is fine for ranking a handful of nearby points.
const dist2 = (aLng, aLat, bLng, bLat) => {
  const dx = aLng - bLng;
  const dy = aLat - bLat;
  return dx * dx + dy * dy;
};

async function fetchNearestImage(lat, lng, signal) {
  const cacheKey = keyFor(lat, lng);
  if (cache.has(cacheKey)) return cache.get(cacheKey);

  const fields = "id,geometry,captured_at,compass_angle,thumb_1024_url";

  for (const half of BBOX_HALF_WIDTHS) {
    const bbox = [lng - half, lat - half, lng + half, lat + half].join(",");
    const url =
      `${GRAPH_URL}?access_token=${encodeURIComponent(MAPILLARY_TOKEN)}` +
      `&fields=${fields}&bbox=${bbox}&limit=50`;

    const res = await fetch(url, { signal });
    if (!res.ok) {
      // 401/403 => bad token; bail out clearly rather than retrying.
      throw new Error(`Mapillary API error ${res.status}`);
    }
    const json = await res.json();
    const images = Array.isArray(json.data) ? json.data : [];
    if (images.length === 0) continue;

    // Pick the image closest to the facility coordinates.
    let best = null;
    let bestD = Infinity;
    for (const img of images) {
      const coords = img.geometry && img.geometry.coordinates;
      if (!coords) continue;
      const d = dist2(coords[0], coords[1], lng, lat);
      if (d < bestD) {
        bestD = d;
        best = img;
      }
    }
    if (best) {
      cache.set(cacheKey, best);
      return best;
    }
  }

  cache.set(cacheKey, null); // remember "nothing nearby"
  return null;
}

const mapillaryAppLink = (image, lat, lng) =>
  `https://www.mapillary.com/app/?pKey=${image.id}` +
  `&focus=photo&lat=${lat}&lng=${lng}&z=17`;

const formatCaptured = (ms) => {
  if (!ms) return null;
  try {
    return new Date(Number(ms)).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
    });
  } catch {
    return null;
  }
};

/**
 * Shows the nearest Mapillary street-level image to a facility.
 * variant="thumbnail" => compact, for the map popup.
 * variant="full"      => larger interactive embed, for the detail modal.
 */
const MapillaryImage = ({ latitude, longitude, variant = "full" }) => {
  const [status, setStatus] = useState("loading"); // loading | found | empty | error
  const [image, setImage] = useState(null);

  const validCoords =
    typeof latitude === "number" &&
    typeof longitude === "number" &&
    !Number.isNaN(latitude) &&
    !Number.isNaN(longitude);

  useEffect(() => {
    if (!MAPILLARY_ENABLED || !validCoords) return;

    const controller = new AbortController();
    let active = true;
    setStatus("loading");
    setImage(null);

    fetchNearestImage(latitude, longitude, controller.signal)
      .then((img) => {
        if (!active) return;
        if (img) {
          setImage(img);
          setStatus("found");
        } else {
          setStatus("empty");
        }
      })
      .catch((err) => {
        if (!active || err.name === "AbortError") return;
        console.error("Mapillary:", err);
        setStatus("error");
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [latitude, longitude, validCoords]);

  // Token not set up yet — only surface this in the full (modal) view so the
  // popup stays clean.
  if (!MAPILLARY_ENABLED) {
    if (variant === "thumbnail") return null;
    return (
      <div className="mapillary-panel mapillary-note">
        <span className="mapillary-label">📷 Street-level imagery</span>
        <p>
          Mapillary imagery isn&apos;t configured yet. Add a Mapillary access
          token to enable it (see <code>MAPILLARY_SETUP.md</code>).
        </p>
      </div>
    );
  }

  if (!validCoords) return null;

  const isThumb = variant === "thumbnail";
  const wrapperClass = isThumb
    ? "mapillary-panel mapillary-thumb"
    : "mapillary-panel mapillary-full";

  if (status === "loading") {
    return (
      <div className={wrapperClass}>
        {!isThumb && <span className="mapillary-label">📷 Street-level imagery</span>}
        <div className="mapillary-loading">Loading nearby imagery…</div>
      </div>
    );
  }

  if (status === "empty") {
    if (isThumb) return null; // keep popups compact when nothing is nearby
    return (
      <div className="mapillary-panel mapillary-note">
        <span className="mapillary-label">📷 Street-level imagery</span>
        <p>No Mapillary street-level imagery found near this location.</p>
      </div>
    );
  }

  if (status === "error") {
    if (isThumb) return null;
    return (
      <div className="mapillary-panel mapillary-note">
        <span className="mapillary-label">📷 Street-level imagery</span>
        <p>Couldn&apos;t load Mapillary imagery right now.</p>
      </div>
    );
  }

  // status === "found"
  const link = mapillaryAppLink(image, latitude, longitude);
  const captured = formatCaptured(image.captured_at);

  if (isThumb) {
    return (
      <a
        className="mapillary-panel mapillary-thumb"
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        title="Open interactive street view on Mapillary"
      >
        <img src={image.thumb_1024_url} alt="Nearest street-level view" />
        <span className="mapillary-thumb-caption">
          📷 Street view{captured ? ` · ${captured}` : ""} ↗
        </span>
      </a>
    );
  }

  return (
    <div className="mapillary-panel mapillary-full">
      <span className="mapillary-label">📷 Street-level imagery</span>
      <div className="mapillary-embed-wrap">
        <iframe
          title="Mapillary street-level view"
          src={`https://www.mapillary.com/embed?image_key=${image.id}&style=photo`}
          frameBorder="0"
          allowFullScreen
        />
      </div>
      <div className="mapillary-caption">
        <span>
          Nearest Mapillary image{captured ? ` · captured ${captured}` : ""}
        </span>
        <a href={link} target="_blank" rel="noopener noreferrer">
          Open in Mapillary ↗
        </a>
      </div>
    </div>
  );
};

export default MapillaryImage;
